import { useState, useEffect, KeyboardEvent, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Typography,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  checkAPIKeyHealth,
  clearAuthError,
  setCredentials,
} from '../store/slices/authSlice';
import type { CheckAPIKeyHealthRequest } from '../store/types';
import {
  VALIDATION_PATTERNS,
  VALIDATION_MESSAGES,
} from '../constants/validationConstants';
import { TIMEOUTS } from '../constants/uiConstants';

const Authentication = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Select auth state from Redux
  const {
    operatorKey: storedOperatorKey,
    email: storedEmail,
    loading,
    error,
    isValid,
    operator,
  } = useAppSelector((state) => state.auth);

  const [email, setEmail] = useState(storedEmail);
  const [operatorKey, setOperatorKey] = useState(storedOperatorKey);
  const [emailError, setEmailError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [disableAuthButton, setDisableAuthButton] = useState(true);

  // Validate email format
  const validateEmail = useCallback((emailValue: string): boolean => {
    if (!emailValue) {
      return false;
    }
    return VALIDATION_PATTERNS.EMAIL.test(emailValue);
  }, []);

  // Handle authentication
  const handleAuthenticate = useCallback(
    (key?: string, emailValue?: string) => {
      const keyToUse = key || operatorKey;
      const emailToUse = emailValue || email;

      if (!keyToUse || !validateEmail(emailToUse)) {
        setEmailError(
          emailToUse && !validateEmail(emailToUse)
            ? VALIDATION_MESSAGES.INVALID_EMAIL
            : ''
        );
        return;
      }

      setEmailError('');
      dispatch(clearAuthError());

      // Update Redux state with credentials
      dispatch(setCredentials({ operatorKey: keyToUse, email: emailToUse }));

      const abortController = new AbortController();
      const request: CheckAPIKeyHealthRequest = {
        params: {
          operatorKey: keyToUse,
          email: emailToUse,
        },
        signal: abortController.signal,
      };

      // Dispatch the action - component will react to state changes via selectors
      // localStorage is handled in the thunk
      dispatch(checkAPIKeyHealth(request));
    },
    [operatorKey, email, dispatch, validateEmail]
  );

  // Load saved credentials from Redux state on mount (already loaded in authSlice)
  useEffect(() => {
    if (storedOperatorKey) {
      setOperatorKey(storedOperatorKey);
    }
    if (storedEmail) {
      setEmail(storedEmail);
    }
  }, [storedOperatorKey, storedEmail]);

  // Check if authentication button should be enabled
  useEffect(() => {
    const isFormValid = operatorKey.trim() !== '' && validateEmail(email);
    setDisableAuthButton(!isFormValid);

    if (email && !validateEmail(email)) {
      setEmailError(VALIDATION_MESSAGES.INVALID_EMAIL);
    } else {
      setEmailError('');
    }
  }, [email, operatorKey, validateEmail]);

  // Clear error when user starts typing
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearAuthError());
      }, TIMEOUTS.ERROR_CLEAR_DELAY);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  // Redirect to ServicePredictor page after successful authentication
  useEffect(() => {
    if (isValid && operator && !loading) {
      navigate('/predictor');
    }
  }, [isValid, operator, loading, navigate]);

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !disableAuthButton) {
      handleAuthenticate();
    }
  };

  return (
    <Paper
      sx={{
        p: 3,
        mb: 3,
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
        Service Availability Predictor
      </Typography>

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          label="Email"
          type="email"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyUp={handleKeyPress}
          error={!!emailError}
          helperText={emailError}
          disabled={loading}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Operator API Key"
          type={showPassword ? 'text' : 'password'}
          name="operatorKey"
          value={operatorKey}
          onChange={(e) => setOperatorKey(e.target.value)}
          onKeyUp={handleKeyPress}
          disabled={loading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  disabled={loading}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => dispatch(clearAuthError())}
        >
          {error}
        </Alert>
      )}

      {isValid && operator && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Successfully Authenticated{operator && ` - Operator: ${operator}`}
        </Alert>
      )}

      <Button
        variant="contained"
        fullWidth
        onClick={() => handleAuthenticate()}
        disabled={disableAuthButton || loading}
        sx={{
          py: 1.5,
          fontSize: '1rem',
          fontWeight: 'bold',
        }}
      >
        {loading ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            Authenticating...
          </>
        ) : (
          'Authenticate'
        )}
      </Button>
    </Paper>
  );
};

export default Authentication;
